/* eslint-disable no-dupe-class-members */

import Vue from 'vue';
import { Store } from 'stores/type';
import debounce from 'lodash/debounce';
import { emitNativeEvent } from 'helpers/events';
import * as EVENTS from 'constants/events';
import { EventName, CustomEvents, EventsWithPayload, EventsWithoutPayload } from 'types';
import { BASE_EVENT_HANDLERS } from './handlers';

export type EventEntry = {
  parent?: 'window' | 'document',
  event: string,
  handler: EventName | ((emit: EventsManager['emit'], event: Event) => void),
};

const EVENT_HANDLERS = [
  ...BASE_EVENT_HANDLERS,
];

// tslint:disable-next-line variable-name
const Events = new Vue({});

class EventsManager {
  private _ready: Promise<void>;

  constructor(eventHandlers: EventEntry[]) {
    this.initEventHandlers(eventHandlers);

    this._ready = new Promise((resolve) => this.once(EVENTS.GAME_READY, resolve));
  }

  public init(store: Store) {
    this.initInitialEvents(store);
    this.initCustomEvents();
  }

  private initInitialEvents(store: Store) {
    // TODO: refactor
    this.once('LIBRARY_RENDERED', () =>
      window.setTimeout(() => this.emit(EVENTS.GAME_READY), 150));
    this.once('LIBRARY_EMPTY', () =>
      window.setTimeout(() => this.emit(EVENTS.GAME_READY), 150));
    this.once('LOGGED_IN', () => this.emit(EVENTS.GAME_READY));
    this.once('NOT_LOGGED_IN', () =>
      window.setTimeout(() => this.emit(EVENTS.GAME_READY), 250));

    this.once(EVENTS.GAME_READY, () => {
      emitNativeEvent(EVENTS.GAME_READY);
      store.commit('GAME_READY');
    });
    document.addEventListener(EVENTS.PLAY, () => this.emit(EVENTS.PLAY));
  }
  private initCustomEvents() {
    const resized = debounce(() => this.emit('RESIZE'), 250);
    window.addEventListener('resize', resized, false);
  }

  private initEventHandlers(eventHandlers: EventEntry[]) {
    eventHandlers.forEach(({ parent = 'window', event, handler }) => {
      const eventParent = parent === 'window' ? window : document;
      let handle;
      if (typeof handler === 'string') {
        handle = () => this.emit(handler as EventsWithoutPayload);
      } else {
        handle = (e: Event) => handler(this.emit, e);
      }
      eventParent.addEventListener(event, handle, false);
    });
  }

  public on<K extends EventsWithoutPayload>(event: K, handler: () => void): void;
  public on<K extends EventsWithPayload>(event: K, handler: (arg: CustomEvents[K]) => void): void;
  public on<K extends keyof CustomEvents>(event: K, handler: (arg: any) => void) {
    Events.$on(event, handler);
  }
  public once<K extends EventsWithoutPayload>(event: K, handler: () => void): void;
  public once<K extends EventsWithPayload>(event: K, handler: (arg: CustomEvents[K]) => void): void;
  public once<K extends keyof CustomEvents>(event: K, handler: (arg: any) => void) {
    Events.$once(event, handler);
  }
  public off(event: EventName, handler: (...args: any[]) => void) {
    Events.$off(event, handler);
  }
  public emit<K extends EventsWithoutPayload>(event: K): void;
  public emit<K extends EventsWithPayload>(event: K, arg: CustomEvents[K]): void;
  public emit<K extends keyof CustomEvents>(event: K, arg?: any): void {
    Events.$emit(event, arg);
  }

  public then<K extends keyof CustomEvents>(event: K): Promise<CustomEvents[K]> {
    return new Promise((resolve) => Events.$once(event, resolve));
  }

  public onResize(handler: () => void) {
    this.on('RESIZE', handler);
  }
  public onReady(): Promise<void> {
    return this._ready;
  }
}

export default new EventsManager(EVENT_HANDLERS);
